import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'

import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface VariableMapping {
  source_node_id: string
  source_output: string
  target_input: string
}

interface VariableBindingFormProps {
  value: VariableMapping[]
  onChange: (mappings: VariableMapping[]) => void
  upstreamNodes: {
    id: string
    label: string
    output_schema: { name: string; type: string }[]
  }[]
  currentNodeInputs: { name: string; type: string }[]
}

const EMPTY_MAPPING: VariableMapping = {
  source_node_id: '',
  source_output: '',
  target_input: '',
}

export function VariableBindingForm({
  value,
  onChange,
  upstreamNodes,
  currentNodeInputs,
}: VariableBindingFormProps) {
  const { t } = useTranslation('workflows')

  const handleAdd = () => {
    onChange([...value, { ...EMPTY_MAPPING }])
  }

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  const handleUpdate = (index: number, partial: Partial<VariableMapping>) => {
    const updated = value.map((mapping, i) =>
      i === index ? { ...mapping, ...partial } : mapping
    )
    onChange(updated)
  }

  const getSourceOutputs = (nodeId: string) => {
    const node = upstreamNodes.find((n) => n.id === nodeId)
    return node?.output_schema ?? []
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold text-gray-200">
          {t('flow.sectionVariableMapping')}
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1 border-gray-700/50 text-gray-300"
          onClick={handleAdd}
          disabled={upstreamNodes.length === 0}
        >
          <Plus className="w-4 h-4" />
          Add mapping
        </Button>
      </div>

      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-2 text-center">
          No variable bindings configured.
        </p>
      ) : (
        <div className="space-y-3">
          {/* Header row */}
          <div className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 items-center text-xs text-gray-500 px-1">
            <span>Target input</span>
            <span>Source node</span>
            <span>Source output</span>
            <span />
          </div>

          {value.map((mapping, index) => {
            const sourceOutputs = getSourceOutputs(mapping.source_node_id)

            return (
              <div
                key={index}
                className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 items-center"
              >
                {/* Target Input */}
                <Select
                  value={mapping.target_input || undefined}
                  onValueChange={(v) =>
                    handleUpdate(index, { target_input: v })
                  }
                >
                  <SelectTrigger className="bg-white/5 border-gray-700/50 h-9 text-sm">
                    <SelectValue placeholder="Select input" />
                  </SelectTrigger>
                  <SelectContent className="border-gray-700/50">
                    {currentNodeInputs.map((input) => (
                      <SelectItem key={input.name} value={input.name}>
                        {input.name} ({input.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Source Node */}
                <Select
                  value={mapping.source_node_id || undefined}
                  onValueChange={(v) =>
                    handleUpdate(index, {
                      source_node_id: v,
                      source_output: '',
                    })
                  }
                >
                  <SelectTrigger className="bg-white/5 border-gray-700/50 h-9 text-sm">
                    <SelectValue placeholder="Select node" />
                  </SelectTrigger>
                  <SelectContent className="border-gray-700/50">
                    {upstreamNodes.map((node) => (
                      <SelectItem key={node.id} value={node.id}>
                        {node.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Source Output */}
                <Select
                  value={mapping.source_output || undefined}
                  onValueChange={(v) =>
                    handleUpdate(index, { source_output: v })
                  }
                  disabled={!mapping.source_node_id}
                >
                  <SelectTrigger className="bg-white/5 border-gray-700/50 h-9 text-sm">
                    <SelectValue placeholder="Select output" />
                  </SelectTrigger>
                  <SelectContent className="border-gray-700/50">
                    {sourceOutputs.map((output) => (
                      <SelectItem key={output.name} value={output.name}>
                        {output.name} ({output.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Remove */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 text-gray-500 hover:text-red-400"
                  onClick={() => handleRemove(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
